import type { PageDoc } from "@/content";
import type { MediaItem } from "@/data/media";
import { getImage } from "@/generated/images";
import { buildMetadata } from "@/lib/seo";
import type { Metadata, OgImage } from "@/types/metadata";

/**
 * A page's search and sharing settings, applied over its route's defaults.
 *
 * The route knows a sensible title and description for itself; the page record
 * is where an editor overrides them, marks a page noindex, points its canonical
 * elsewhere or chooses the picture a shared link shows. Used on the server to
 * write the head and in the browser to keep it right across navigation, so the
 * two cannot disagree.
 */
export function applyPageMeta(
  base: Metadata | null,
  {
    page,
    pathname,
    media,
    titleIsComplete = false,
  }: { page: PageDoc | null | undefined; pathname: string; media: Record<string, MediaItem>; titleIsComplete?: boolean },
): Metadata | null {
  if (!page) return base;
  const shareImage = page.socialImageKey ? ogImageFor(media[page.socialImageKey]) : undefined;

  return buildMetadata({
    title: page.metaTitle || page.title || base?.title || "",
    description: page.metaDescription || base?.description || "",
    path: pathname,
    titleIsComplete,
    canonical: page.canonicalUrl || undefined,
    noindex: Boolean(page.noindex),
    image: shareImage ?? base?.openGraph.images[0],
    type: base?.openGraph.type,
    publishedTime: base?.openGraph.publishedTime,
  });
}

/**
 * The picture a shared link shows, at the size link previews expect.
 *
 * Social networks crop to about 1.91:1 and want at least 1200px across, so the
 * smallest version that wide is chosen, or the largest there is. A photograph
 * nobody has described, or one whose consent check is outstanding, is never a
 * share image: a preview card is publication like any other.
 */
export function ogImageFor(item?: MediaItem | null): OgImage | undefined {
  if (!item?.alt) return undefined;
  if (item.needsConsentCheck && item.consentStatus !== "granted") return undefined;

  if (item.kind === "upload") {
    const candidates = (item.variants ?? [])
      .filter((variant) => variant.format === "jpeg" || variant.format === "png")
      .sort((a, b) => a.width - b.width);
    const chosen = candidates.find((variant) => variant.width >= 1200) ?? candidates.at(-1);
    if (chosen) {
      const ratio = item.width && item.height ? item.height / item.width : 630 / 1200;
      return { url: chosen.url, width: chosen.width, height: chosen.height ?? Math.round(chosen.width * ratio), alt: item.alt };
    }
    return item.url ? { url: item.url, width: item.width ?? 1200, height: item.height ?? 630, alt: item.alt } : undefined;
  }

  if (!item.asset) return undefined;
  try {
    const image = getImage(item.asset);
    const ladder = image.fallback
      .split(",")
      .map((entry) => entry.trim().split(/\s+/))
      .map(([url, descriptor]) => ({ url, width: Number.parseInt(descriptor ?? "", 10) }))
      .filter((entry) => entry.url && Number.isFinite(entry.width))
      .sort((a, b) => a.width - b.width);
    const chosen = ladder.find((entry) => entry.width >= 1200) ?? ladder.at(-1);
    if (!chosen) return undefined;
    return {
      url: chosen.url,
      width: chosen.width,
      height: Math.round((chosen.width * image.height) / image.width),
      alt: item.alt,
    };
  } catch {
    return undefined;
  }
}
